import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { TimeSpan, type ITimeSpan } from '@/objects/TimeSpan';
import type { EntityState } from './../enums/EntityState';

export class Timepenalty extends BaseObj {
    public ID: number;
    public __id: number;

    public Deleted: boolean;
    public __deleted: boolean;

    public EventID: number;
    public __eventid: number;

    public Km: number;
    public __km: number;

    public Penalty: ITimeSpan;
    public __penalty: ITimeSpan;

    public RaceContestantID: number;
    public __racecontestantid: number;

    public Reason: string | null;
    public __reason: string | null;

    public StationRoleID: number | null;
    public __stationroleid: number | null;

    public TimepenaltyTypeID: number;
    public __timepenaltytypeid: number;

    public TUID: string | null;
    public __tuid: string | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Deleted?: boolean;
        EntityState?: EntityState;
        EventID?: number;
        Km?: number;
        Penalty?: ITimeSpan;
        RaceContestantID?: number;
        Reason?: string | null;
        StationRoleID?: number | null;
        TimepenaltyTypeID?: number;
        TUID?: string | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<Timepenalty>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('Timepenalty tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Deleted = values.Deleted ?? false;
        this.__deleted = values.Deleted ?? false;
        Object.defineProperty(this, 'Deleted', {
            get(): boolean {
                return this.__deleted;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Deleted', this.__deleted, value);
                this.__deleted = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Km = values.Km ?? 0;
        this.__km = values.Km ?? 0;
        Object.defineProperty(this, 'Km', {
            get(): number {
                return this.__km;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Km', this.__km, value);
                this.__km = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Penalty = values.Penalty ?? TimeSpan.Empty();
        this.__penalty = values.Penalty ?? TimeSpan.Empty();
        Object.defineProperty(this, 'Penalty', {
            get(): ITimeSpan {
                return this.__penalty;
            },
            set(value: ITimeSpan) {
                this.__entityStateHandler.propertyChanged('Penalty', this.__penalty, value);
                this.__penalty = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceContestantID = values.RaceContestantID ?? 0;
        this.__racecontestantid = values.RaceContestantID ?? 0;
        Object.defineProperty(this, 'RaceContestantID', {
            get(): number {
                return this.__racecontestantid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('RaceContestantID', this.__racecontestantid, value);
                this.__racecontestantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Reason = values.Reason ?? null;
        this.__reason = values.Reason ?? null;
        Object.defineProperty(this, 'Reason', {
            get(): string | null {
                return this.__reason;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Reason', this.__reason, value);
                this.__reason = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StationRoleID = values.StationRoleID ?? null;
        this.__stationroleid = values.StationRoleID ?? null;
        Object.defineProperty(this, 'StationRoleID', {
            get(): number | null {
                return this.__stationroleid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('StationRoleID', this.__stationroleid, value);
                this.__stationroleid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TimepenaltyTypeID = values.TimepenaltyTypeID ?? 0;
        this.__timepenaltytypeid = values.TimepenaltyTypeID ?? 0;
        Object.defineProperty(this, 'TimepenaltyTypeID', {
            get(): number {
                return this.__timepenaltytypeid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('TimepenaltyTypeID', this.__timepenaltytypeid, value);
                this.__timepenaltytypeid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TUID = values.TUID ?? null;
        this.__tuid = values.TUID ?? null;
        Object.defineProperty(this, 'TUID', {
            get(): string | null {
                return this.__tuid;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('TUID', this.__tuid, value);
                this.__tuid = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Deleted": ${JSON.stringify(this.Deleted)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"Km": ${JSON.stringify(this.Km)}` +
            ',' +
            `"Penalty": ${'"' + this.Penalty?.serialize() + '"'}` +
            ',' +
            `"RaceContestantID": ${JSON.stringify(this.RaceContestantID)}` +
            ',' +
            `"Reason": ${JSON.stringify(this.Reason)}` +
            ',' +
            `"StationRoleID": ${JSON.stringify(this.StationRoleID)}` +
            ',' +
            `"TimepenaltyTypeID": ${JSON.stringify(this.TimepenaltyTypeID)}` +
            ',' +
            `"TUID": ${JSON.stringify(this.TUID)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of Timepenalty from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Deleted?: boolean;
        EventID?: number;
        Km?: number;
        Penalty?: ITimeSpan;
        RaceContestantID?: number;
        Reason?: string | null;
        StationRoleID?: number | null;
        TimepenaltyTypeID?: number;
        TUID?: string | null;
        EntityState?: 'Added' | 'Unchanged';
    }): Timepenalty {
        return new Timepenalty(values);
    }

    public static _FromServerJson(json: any): Timepenalty {
        return new Timepenalty({
            ID: json.ID,
            Deleted: json.Deleted,
            EventID: json.EventID,
            Km: json.Km,
            Penalty: json.Penalty ? TimeSpan.FromTimeString(json.Penalty) : TimeSpan.Empty(),
            RaceContestantID: json.RaceContestantID,
            Reason: json.Reason ?? '',
            StationRoleID: json.StationRoleID ?? null,
            TimepenaltyTypeID: json.TimepenaltyTypeID,
            TUID: json.TUID ?? '',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of Timepenalty from another instance of Timepenalty.
     * @param other The Timepenalty you want to clone
     * @returns A cloned Timepenalty
     */
    public static FromOther(other: Timepenalty): Timepenalty {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return Timepenalty._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return Timepenalty.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'Deleted'
            | 'EventID'
            | 'Km'
            | 'Penalty'
            | 'RaceContestantID'
            | 'Reason'
            | 'StationRoleID'
            | 'TimepenaltyTypeID'
            | 'TUID'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'Timepenalty';
}
